import { useMutation, useQuery } from '@apollo/react-hooks';
import { ApolloError } from 'apollo-client';
import React, { useCallback, useEffect, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { useHistory } from 'react-router-dom';

import {
  ConfirmationModal,
  ErrorMessage,
  ErrorModal,
} from '../../../components';
import { routePaths } from '../../../constants/routes';
import { Button, Table, Text } from '../../../core-ui';
import {
  CancelMissionTopupReward,
  CancelMissionTopupRewardVariables,
} from '../../../generated/CancelMissionTopupReward';
import { StatusBulkPoint } from '../../../generated/globalTypes';
import {
  MissionTopupReward,
  MissionTopupRewardVariables,
} from '../../../generated/MissionTopupReward';
import {
  SendMissionPointToDB,
  SendMissionPointToDBVariables,
} from '../../../generated/SendMissionPointToDB';
import {
  CANCEL_MISSION_TOPUP_REWARD,
  SEND_MISSION_POINT_TO_DB,
} from '../../../graphql/mutations';
import { GET_MISSION_TOPUP_REWARD } from '../../../graphql/queries';

type Props = { refetchToggle: boolean; onSubmit: () => void };

export const TopUpRewardTable = ({ refetchToggle, onSubmit }: Props) => {
  const history = useHistory();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [missionTopupRewardId, setMissionTopupRewardId] = useState('');
  const [cancelOpen, setCancelOpen] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [errorOpen, setErrorOpen] = useState(false);
  const [errorAction, setErrorAction] = useState('');
  const [errorInstance, setErrorInstance] = useState<ApolloError | undefined>();

  const closeSendModal = useCallback(() => setSendOpen(false), []);
  const closeCancelModal = useCallback(() => setCancelOpen(false), []);
  const closeErrorModal = useCallback(() => setErrorOpen(false), []);

  const onChangeRowsPerPage = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      setRowsPerPage(Number(event.currentTarget.value));
      setPage(0);
    },
    [setRowsPerPage, setPage],
  );

  const onSubmitCompleted = useCallback(() => {
    setSendOpen(false);
    setCancelOpen(false);
    setMissionTopupRewardId('');
    onSubmit();
  }, [onSubmit]);

  const onSendError = useCallback((error: ApolloError) => {
    setErrorOpen(true);
    setErrorAction(t(['mengirim data ke database', 'send data to database']));
    setErrorInstance(error);
  }, []);
  const onCancelError = useCallback((error: ApolloError) => {
    setErrorOpen(true);
    setErrorAction(t(['mengunggah berkas', 'upload file']));
    setErrorInstance(error);
  }, []);

  const [sendMissionPoint, { loading: sendLoading }] = useMutation<
    SendMissionPointToDB,
    SendMissionPointToDBVariables
  >(SEND_MISSION_POINT_TO_DB, {
    onCompleted: onSubmitCompleted,
    onError: onSendError,
  });

  const [cancelMissionReward, { loading: cancelLoading }] = useMutation<
    CancelMissionTopupReward,
    CancelMissionTopupRewardVariables
  >(CANCEL_MISSION_TOPUP_REWARD, {
    onCompleted: onSubmitCompleted,
    onError: onCancelError,
  });

  const { data, loading, error, refetch } = useQuery<
    MissionTopupReward,
    MissionTopupRewardVariables
  >(GET_MISSION_TOPUP_REWARD, {
    pollInterval: 5000,
    variables: {
      first: rowsPerPage,
      skip: page * rowsPerPage,
    },
  });

  const refetchData = useCallback(
    (skip: number = page * rowsPerPage) => {
      const asyncRefetch = async () => {
        refetch({ skip, first: rowsPerPage });
      };
      asyncRefetch();
    },
    [page, rowsPerPage, refetch],
  );

  useEffect(() => {
    refetchData();
  }, [refetchData, refetchToggle]);

  const onSendPress = useCallback((id: string) => {
    setMissionTopupRewardId(id);
    setSendOpen(true);
  }, []);
  const onConfirmSend = useCallback(() => {
    sendMissionPoint({ variables: { missionTopupRewardId } });
  }, [missionTopupRewardId, sendMissionPoint]);

  const onCancelPress = useCallback((id: string) => {
    setMissionTopupRewardId(id);
    setCancelOpen(true);
  }, []);
  const onConfirmCancel = useCallback(() => {
    cancelMissionReward({ variables: { missionTopupRewardId } });
  }, [missionTopupRewardId, cancelMissionReward]);

  return (
    <>
      <ConfirmationModal
        keepOpenAfterConfirm
        title={t([
          'Apakah anda yakin ingin mengirim data ke database?',
          'Are you sure you want to send this data to database?',
        ])}
        open={sendOpen}
        onClose={closeSendModal}
        onConfirm={onConfirmSend}
        loading={sendLoading}
      />
      <ConfirmationModal
        keepOpenAfterConfirm
        title={t([
          'Apakah anda yakin ingin batal mengirim data ke database?',
          'Are you sure you want to cancel sending this data to database?',
        ])}
        open={cancelOpen}
        onClose={closeCancelModal}
        onConfirm={onConfirmCancel}
        loading={cancelLoading}
      />
      <ErrorModal
        open={errorOpen}
        action={errorAction}
        error={errorInstance}
        onClose={closeErrorModal}
      />
      <Table
        data-testid="bulk-point-table"
        data={data?.missionTopupReward.row ?? []}
        dataCount={data?.missionTopupReward.total ?? 0}
        loading={loading}
        error={!!error}
        page={page}
        onChangePage={setPage}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[25, 50, 100]}
        onChangeRowsPerPage={onChangeRowsPerPage}
        structure={{
          createdAt: {
            headerTitle: t(['Tanggal Unggah', 'Upload Date']),
            valuePreProcessor: (data) =>
              data ? new Date(data as string) : null,
          },
          fileName: {
            'data-testid': 'table-file-name',
            headerTitle: t(['Nama Berkas', 'File Name']),
          },
          status: {
            'data-testid': 'table-status',
            headerTitle: 'Status',
            render: ({ status }) => {
              switch (status) {
                case StatusBulkPoint.ON_PROGRESS:
                  return (
                    <Text bold color="pending">
                      {t(['Sedang Diproses', 'On Progress'])}
                    </Text>
                  );
                case StatusBulkPoint.ON_COMMIT:
                  return (
                    <Text bold color="pending">
                      {t(['Sedang Dikirim', 'On Commit'])}
                    </Text>
                  );
                case StatusBulkPoint.READY_TO_REVIEW:
                  return (
                    <Text bold color="link">
                      {t(['Siap Diperiksa', 'Ready for Review'])}
                    </Text>
                  );
                case StatusBulkPoint.DONE:
                  return (
                    <Text bold color="accepted">
                      {t(['Selesai', 'Done'])}
                    </Text>
                  );
                case StatusBulkPoint.CANCELLED:
                  return (
                    <Text bold color="error">
                      {t(['Dibatalkan', 'Cancelled'])}
                    </Text>
                  );
                default:
                  return (
                    <Text bold color="error">
                      {t(['Gagal', 'Failed'])}
                    </Text>
                  );
              }
            },
          },
          detail: {
            align: 'center',
            headerTitle: t(['Rincian', 'Detail']),
            render: ({ id, status, countValid, countInvalid, detail }) => {
              switch (status) {
                case StatusBulkPoint.READY_TO_REVIEW:
                  return (
                    <Button
                      data-testid="details-table-button"
                      preset="transparent"
                      onPress={() =>
                        history.push(routePaths.invalidDataHistory, {
                          id: id.toString(),
                          statusPoint: status,
                        })
                      }
                      title={t(
                        [
                          '{valid}{validText}{separator}{invalid}{invalidTextId}',
                          '{valid}{validText}{separator}{invalid}{invalidTextEn}',
                        ],
                        {
                          valid: countValid || '',
                          invalid: countInvalid || '',
                          validText: !!countValid ? ' valid' : '',
                          separator: !!countValid && !!countInvalid ? ', ' : '',
                          invalidTextId: !!countInvalid ? ' tidak valid' : '',
                          invalidTextEn: !!countInvalid ? ' invalid' : '',
                        },
                      )}
                    />
                  );
                case StatusBulkPoint.FAIL: {
                  if (detail === 'Format header is different') {
                    return (
                      <Text>
                        {t([
                          'Format dokumen tidak sesuai',
                          'Invalid file format',
                        ])}
                      </Text>
                    );
                  } else if (detail === 'Occur error from system') {
                    return (
                      <Text>
                        {t([
                          'Terjadi kesalahan saat memproses dokumen',
                          'An error occured while processing the file',
                        ])}
                      </Text>
                    );
                  } else if (detail === 'Invalid Row') {
                    return (
                      <Button
                        data-testid="details-table-button"
                        preset="transparent"
                        onPress={() =>
                          history.push(routePaths.invalidDataHistory, {
                            id: id.toString(),
                            statusPoint: status,
                          })
                        }
                        title={t(
                          [
                            '{valid}{validText}{separator}{invalid}{invalidTextId}',
                            '{valid}{validText}{separator}{invalid}{invalidTextEn}',
                          ],
                          {
                            valid: countValid || '',
                            invalid: countInvalid || '',
                            validText: !!countValid ? ' valid' : '',
                            separator:
                              !!countValid && !!countInvalid ? ', ' : '',
                            invalidTextId: !!countInvalid ? ' tidak valid' : '',
                            invalidTextEn: !!countInvalid ? ' invalid' : '',
                          },
                        )}
                      />
                    );
                  }
                }
                default:
                  return (
                    <Text bold color="link">
                      -
                    </Text>
                  );
              }
            },
          },
          action: {
            align: 'center',
            headerTitle: t(['Aksi', 'Action']),
            render: ({ id, status, countInvalid, detail }) =>
              status === StatusBulkPoint.READY_TO_REVIEW ? (
                <View style={styles.actionRow}>
                  <Button
                    preset="transparent"
                    title={t(['kirim ke database', 'send to database'])}
                    onPress={() => onSendPress(id.toString())}
                    disabled={sendLoading || cancelLoading || !!countInvalid}
                    isLoading={sendLoading && missionTopupRewardId === id}
                  />
                  <Text bold color="link">
                    {' '}
                    |{' '}
                  </Text>
                  <Button
                    preset="transparent"
                    title={t(['batal', 'cancel'])}
                    onPress={() => onCancelPress(id.toString())}
                    disabled={sendLoading || cancelLoading}
                    isLoading={cancelLoading && missionTopupRewardId === id}
                  />
                </View>
              ) : detail === 'Invalid Row' ? (
                <View style={styles.actionRow}>
                  <Button
                    preset="transparent"
                    title={t(['silahkan cek kembali', 'please review again'])}
                    disabled
                  />
                </View>
              ) : (
                <Text bold color="link">
                  -
                </Text>
              ),
          },
        }}
      />
      {!!error && (
        <ErrorMessage
          error={error}
          action="mengambil data top up mission Reward"
          onPress={refetchData}
        />
      )}
    </>
  );
};

const styles = StyleSheet.create({
  actionRow: { flexDirection: 'row', alignItems: 'center' },
});
